// Client-side image compression using canvas.
// Resizes to a max long edge and re-encodes to JPEG for smaller uploads,
// which is essential on 3G/4G networks (RDC).
//
// Usage:
//   const smaller = await compressImage(file);
//   uploadFormData.append('file', smaller);

const DEFAULT_MAX_EDGE = 1600;
const DEFAULT_QUALITY = 0.8;
// Only bother compressing if the file is larger than this threshold.
const MIN_SIZE_FOR_COMPRESSION = 400 * 1024; // 400 KB

/**
 * Compress an image File / Blob.
 * - Non-images are returned untouched.
 * - Images already smaller than MIN_SIZE_FOR_COMPRESSION are returned untouched.
 * - Otherwise the image is downscaled (long edge <= maxEdge) and re-encoded
 *   as JPEG at the given quality. If the result is somehow bigger than the
 *   original, the original is returned.
 *
 * @param {File} file
 * @param {{ maxEdge?: number, quality?: number }} [opts]
 * @returns {Promise<File>}
 */
export async function compressImage(file, opts = {}) {
  if (!file || !file.type || !file.type.startsWith('image/')) return file;
  if (file.type === 'image/gif' || file.type === 'image/svg+xml') return file;
  if (file.size < MIN_SIZE_FOR_COMPRESSION) return file;

  const maxEdge = opts.maxEdge || DEFAULT_MAX_EDGE;
  const quality = opts.quality || DEFAULT_QUALITY;

  const dataUrl = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const img = await new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = dataUrl;
  });

  let { width, height } = img;
  const longest = Math.max(width, height);
  if (longest > maxEdge) {
    const ratio = maxEdge / longest;
    width = Math.round(width * ratio);
    height = Math.round(height * ratio);
  }

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0, width, height);

  const blob = await new Promise((resolve) => {
    canvas.toBlob(resolve, 'image/jpeg', quality);
  });

  if (!blob || blob.size >= file.size) return file;

  const base = (file.name || 'photo').replace(/\.[^.]+$/, '');
  return new File([blob], `${base}.jpg`, {
    type: 'image/jpeg',
    lastModified: Date.now(),
  });
}
